import React, { useCallback, useEffect, useState } from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './MiPedido.css';
import 'jspdf-autotable';
import baseURL from '../url';
import moneda from '../moneda';
import Modal from 'react-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faSearch } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import { searchImageUrl } from '../imageUrl';

const MENSAJE_PEDIDO_INVALIDO = 'Pedido no encontrado o verificación inválida';

export default function MiPedido({ autoOpenPedidoId = '', triggerLabel = 'Ver mi pedido' }) {
    const [isFocused, setIsFocused] = useState(false);
    const [idPedido, setIdPedido] = useState(localStorage.getItem('idPedido') || '');
    const [verificacionPedido, setVerificacionPedido] = useState('');
    const [pedidoDetalle, setPedidoDetalle] = useState(null);
    const [modalIsOpen, setModalIsOpen] = useState(false);

    const normalizarIdPedido = useCallback((value) => String(value ?? '').trim(), []);
    const normalizarVerificacion = useCallback((value) => String(value ?? '').replace(/\D+/g, ''), []);

    const obtenerTokenConsulta = useCallback((pedidoId = idPedido) => {
        const pedidoBuscado = normalizarIdPedido(pedidoId);

        if (!pedidoBuscado) return '';

        try {
            return localStorage.getItem(`pedidoConsultaToken:${pedidoBuscado}`) || '';
        } catch (error) {
            return '';
        }
    }, [idPedido, normalizarIdPedido]);

    const leerProductosPedido = (productosPedido) => {
        if (Array.isArray(productosPedido)) return productosPedido;

        try {
            const productosParseados = JSON.parse(productosPedido || '[]');
            return Array.isArray(productosParseados) ? productosParseados : [];
        } catch (error) {
            console.error('Error al leer productos del pedido:', error);
            return [];
        }
    };

    const productosDelPedido = pedidoDetalle ? leerProductosPedido(pedidoDetalle.productos) : [];
    const requiereVerificacion = Boolean(normalizarIdPedido(idPedido)) && !obtenerTokenConsulta(idPedido);

    const consultarPedido = useCallback(async ({ mostrarAlerta = false } = {}) => {
        const pedidoId = normalizarIdPedido(idPedido);

        if (!pedidoId) {
            setPedidoDetalle(null);
            if (mostrarAlerta) {
                Swal.fire({
                    title: 'Pedido no encontrado',
                    text: MENSAJE_PEDIDO_INVALIDO,
                    icon: 'error',
                    confirmButtonText: 'Aceptar'
                });
            }
            return;
        }

        const tokenConsulta = obtenerTokenConsulta(pedidoId);
        const verificacion = normalizarVerificacion(verificacionPedido);

        if (!tokenConsulta && !verificacion) {
            setPedidoDetalle(null);
            if (mostrarAlerta) {
                Swal.fire({
                    title: 'Verificación requerida',
                    text: 'Para pedidos anteriores ingresa el teléfono o cédula usado en el pedido.',
                    icon: 'info',
                    confirmButtonText: 'Aceptar'
                });
            }
            return;
        }

        const params = new URLSearchParams({ idPedido: pedidoId });
        if (tokenConsulta) {
            params.set('tokenConsulta', tokenConsulta);
        } else {
            params.set('verificacion', verificacion);
        }

        try {
            const response = await fetch(`${baseURL}/pedidoPublicGet.php?${params.toString()}`, {
                method: 'GET',
            });
            const data = await response.json();

            if (!response.ok || data?.error || !data?.pedido) {
                throw new Error(data?.error || MENSAJE_PEDIDO_INVALIDO);
            }

            setPedidoDetalle(data.pedido);
            if (mostrarAlerta) {
                Swal.fire({
                    title: 'Pedido encontrado',
                    text: `ID Pedido: ${data.pedido.idPedido}, Nombre: ${data.pedido.nombre}`,
                    icon: 'success',
                    confirmButtonText: 'Aceptar'
                });
            }
        } catch (error) {
            setPedidoDetalle(null);
            if (mostrarAlerta) {
                Swal.fire({
                    title: 'Pedido no encontrado',
                    text: MENSAJE_PEDIDO_INVALIDO,
                    icon: 'error',
                    confirmButtonText: 'Aceptar'
                });
            }
        }
    }, [idPedido, normalizarIdPedido, normalizarVerificacion, obtenerTokenConsulta, verificacionPedido]);

    useEffect(() => {
        cargarProductos();
    }, []);

    useEffect(() => {
        // Si existe un idPedido en localStorage, intenta buscarlo automáticamente al abrir el modal
        if (modalIsOpen && normalizarIdPedido(idPedido) && obtenerTokenConsulta(idPedido)) {
            consultarPedido();
        }
    }, [modalIsOpen, idPedido, consultarPedido, normalizarIdPedido, obtenerTokenConsulta]);


    useEffect(() => {
        const pedidoId = normalizarIdPedido(autoOpenPedidoId);

        if (pedidoId) {
            setIdPedido(pedidoId);
            setVerificacionPedido('');
            setModalIsOpen(true);
            setIsFocused(true);
        }
    }, [autoOpenPedidoId, normalizarIdPedido]);

    const openModal = () => {
        setModalIsOpen(true);
        setIsFocused(true);
    };

    const closeModal = () => {
        setModalIsOpen(false);
        setIsFocused(false);
        setPedidoDetalle(null);
    };

    const handleInputChange = (e) => {
        const value = e.target.value;
        setIdPedido(value);
        setVerificacionPedido('');

        if (!normalizarIdPedido(value)) {
            setPedidoDetalle(null);
        }
    };

    const handleVerificacionChange = (e) => {
        setVerificacionPedido(e.target.value);
        setPedidoDetalle(null);
    };



    // Función para buscar pedido con alerta
    const buscarPedidoConAlerta = () => {
        consultarPedido({ mostrarAlerta: true });
    };

    const [cartItems, setCartItems] = useState([]);
    const [productos, setProductos] = useState([]);
    const [, setLoading] = useState(true);


    const cargarProductos = () => {
        fetch(`${baseURL}/productosGet.php`, {
            method: 'GET',
        })
            .then(response => response.json())
            .then(data => {
                setProductos(data.productos || []);
            })
            .catch(error => console.error('Error al cargar productos:', error));
    };

    useEffect(() => {
        const fetchCartItems = async () => {
            const cart = JSON.parse(localStorage.getItem('cart')) || [];
            const promises = cart.map(async (cartItem) => {
                const producto = productos.find(producto => producto.idProducto === cartItem.idProducto);
                return {
                    ...producto,
                    cantidad: cartItem.cantidad,
                    item: cartItem.item,
                };
            });

            Promise.all(promises)
                .then((items) => {
                    setCartItems(items);
                    setLoading(false);
                })
                .catch((error) => {
                    console.error('Error al obtener detalles del carrito:', error);
                    setLoading(false);
                });
        };

        fetchCartItems();
    }, [productos, isFocused]);

    const obtenerImagen = (item) => {
        return item.imagen1 || item.imagen2 || item.imagen3 || item.imagen4 || null;
    };





    return (
        <div>
            {triggerLabel && <button onClick={openModal}>{triggerLabel}</button>}
            <ToastContainer />
            <Modal
                isOpen={modalIsOpen}
                className="modal-cart"
                overlayClassName="overlay-cart"
                onRequestClose={closeModal}
            >
                <div className='deFLex'>
                    <button onClick={closeModal}>
                        <FontAwesomeIcon icon={faArrowLeft} />
                    </button>
                    <button className='deleteToCart'>Mi pedido</button>
                </div>
                <div className='paddingConten'>
                    <fieldset className='inputSearch'>
                        <input
                            type="number"
                            placeholder="Ingrese N° de Pedido"
                            value={idPedido}
                            onChange={handleInputChange}
                            className="input"
                        />
                        <FontAwesomeIcon icon={faSearch} onClick={buscarPedidoConAlerta} className="search-icon" />
                    </fieldset>
                    {requiereVerificacion && (
                        <fieldset className='inputSearch'>
                            <input
                                type="text"
                                placeholder="Teléfono o cédula del pedido"
                                value={verificacionPedido}
                                onChange={handleVerificacionChange}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') {
                                        buscarPedidoConAlerta();
                                    }
                                }}
                                className="input"
                            />
                        </fieldset>
                    )}
                </div>
                {pedidoDetalle && (
                    <div className='MiPedidoContain'>


                        <div className="modal-content-cart">

                            <div className='deFlexSpanPedido'>
                                <span>{pedidoDetalle.nombre}</span>
                                {pedidoDetalle.cedula && <span>CC {pedidoDetalle.cedula}</span>}
                                <span>{pedidoDetalle.estado}</span>
                                <span>Pedido: {pedidoDetalle.idPedido}</span>
                                <span>Pagado: {pedidoDetalle.pagado}</span>
                                <span>{pedidoDetalle.entrega}</span>
                                {pedidoDetalle.ciudad && <span>{pedidoDetalle.ciudad}</span>}
                                <span>{new Date(pedidoDetalle?.createdAt)?.toLocaleString('es-ES', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric'
                                })}</span>
                            </div>
                            <div className='cardsProductData'>
                                {productosDelPedido.length > 0 ? (
                                    productosDelPedido.map((producto, index) => (
                                        <div key={`${producto.idProducto || producto.titulo || 'producto'}-${index}`} className='cardProductData'>
                                            <img src={searchImageUrl(producto.imagen)} alt="imagen" loading="lazy" decoding="async" />
                                            <div className='cardProductDataText'>

                                                <h3>{producto.titulo}</h3>
                                                <strong>{moneda} {producto.precio} <span>x{producto.cantidad}</span></strong>
                                                <span>
                                                    {producto?.items?.map((sabor, itemIndex) => (
                                                        <span key={itemIndex}>{sabor}, </span>
                                                    ))}
                                                </span>
                                                <h5>{producto.categoria}</h5>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <p className='nohay'>Este pedido no tiene productos registrados.</p>
                                )}
                            </div>

                            <fieldset className='deNonefieldset'>
                                <legend>Productos</legend>
                                <textarea
                                    name='productos'
                                    value={cartItems.map(item => ` ${item.categoria}, ${item.titulo}, x ${item.cantidad}, ${item.item}, ${item.precio}, ${obtenerImagen(item)} `).join('\n')}
                                    readOnly
                                />
                            </fieldset>
                        </div>
                        <div className='deColumnCart'>
                            {pedidoDetalle?.subtotal && (
                                <h4>Subtotal: {moneda} {pedidoDetalle.subtotal}</h4>
                            )}
                            {pedidoDetalle?.valorDomicilio && Number(pedidoDetalle.valorDomicilio) > 0 && (
                                <h4>Domicilio: {moneda} {pedidoDetalle.valorDomicilio}</h4>
                            )}
                            <h4>Total: {moneda} {pedidoDetalle && (
                                pedidoDetalle?.total
                            )}

                            </h4>

                        </div>

                    </div>
                )}



            </Modal>
        </div>
    );
}
